/**
 * Table builder modal.
 *
 * Opens from the markdown editor's toolbar. Authors compose a table in a
 * spreadsheet-style grid, then "Insert table" generates GFM markdown and
 * splices it into the textarea at the cursor (or replaces an existing
 * table block when opened in edit-mode).
 *
 * Cells are raw markdown text — the live preview at the bottom routes
 * through `ArticleBody`, so `{+2.8}` delta chips, **bold**, *italic*,
 * and inline links all render exactly as they will in the published
 * article. The cell toolbar wraps the focused cell's selection in
 * `**`/`*` or inserts a delta-chip template at the caret.
 *
 * Cells are multi-line: Enter creates a line break inside the cell
 * (the "stack a value over a delta chip" pattern — `6.8%\n{+0.6%}`).
 * In-cell newlines are serialized to `<br>` in the GFM source and
 * decoded back on parse, since GFM tables disallow literal newlines.
 *
 * Whole-table options:
 *   - "Center on page" checkbox — emits a `{table:center}` directive
 *     line that `remarkTableDirectives` consumes to apply
 *     `margin-left/right: auto`.
 *   - Per-column "Primary" toggle (star icon next to the alignment
 *     buttons) — tags that column to render with the same gray-fill +
 *     bold chrome as the header row. Useful for tables whose first
 *     column carries row labels rather than data.
 *
 * Keyboard:
 *   - Tab / Shift+Tab move between cells (wrapping rows)
 *   - Enter creates an in-cell line break (no longer adds a new row;
 *     the + Row button is the row-add affordance)
 *
 * Bounded scope: no drag-reorder for rows/columns, no per-cell color
 * formatting beyond what delta chips already provide.
 */

import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import FormatAlignCenterIcon from "@mui/icons-material/FormatAlignCenter";
import FormatAlignLeftIcon from "@mui/icons-material/FormatAlignLeft";
import FormatAlignRightIcon from "@mui/icons-material/FormatAlignRight";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import RemoveIcon from "@mui/icons-material/Remove";
import StarOutlineIcon from "@mui/icons-material/StarOutline";

import ArticleBody from "../ArticleBody";
import {
  addColumn,
  addRow,
  emptyTable,
  removeColumn,
  removeRow,
  setAlignment,
  setCell,
  setCenter,
  setHeader,
  TableAlignment,
  TableModel,
  tableModelToMarkdown,
  togglePrimaryCol,
} from "./tableModel";

export interface TableBuilderDialogProps {
  open: boolean;
  onClose: () => void;
  /** Pre-fill the dialog with an existing table (edit-mode). When
   *  omitted, the dialog opens with a fresh 2x3 empty table. */
  initial?: TableModel;
  /** Receives the generated GFM markdown to insert at the cursor. */
  onInsert: (markdown: string) => void;
}

/** Identifies a cell within the model. `row === -1` is the header row. */
interface CellAddress {
  row: number;
  col: number;
}

const DEFAULT_INITIAL = (): TableModel => emptyTable(2, 3);

export default function TableBuilderDialog({
  open,
  onClose,
  initial,
  onInsert,
}: TableBuilderDialogProps) {

  // Local table state, reset every time the dialog (re-)opens.
  const [model, setModel] = useState<TableModel>(
    () => initial ?? DEFAULT_INITIAL(),
  );
  useEffect(() => {
    if (open) {
      setModel(initial ?? DEFAULT_INITIAL());
      // Clear the focused-cell address so a stale ref from a previous
      // open can't make the cell toolbar target a now-nonexistent cell.
      setFocused(null);
    }
  }, [open, initial]);

  // Track which cell currently has keyboard focus so the cell toolbar
  // can target the correct TextField. The address-keyed map of refs
  // lets us reach into the focused field to read selection + write
  // the wrapped/inserted text.
  const [focused, setFocused] = useState<CellAddress | null>(null);
  const cellRefs = useRef<Map<string, HTMLTextAreaElement | HTMLInputElement>>(
    new Map(),
  );
  const cellKey = (a: CellAddress) => `${a.row}:${a.col}`;

  const setRef = (
    addr: CellAddress,
    el: HTMLTextAreaElement | HTMLInputElement | null,
  ) => {
    const key = cellKey(addr);
    if (el) cellRefs.current.set(key, el);
    else cellRefs.current.delete(key);
  };

  const readCellValue = (addr: CellAddress): string =>
    addr.row === -1
      ? model.headers[addr.col] ?? ""
      : model.rows[addr.row]?.[addr.col] ?? "";

  const writeCellValue = (addr: CellAddress, next: string) => {
    setModel((prev) =>
      addr.row === -1
        ? setHeader(prev, addr.col, next)
        : setCell(prev, addr.row, addr.col, next),
    );
  };

  // ---- Cell toolbar ops (apply to focused cell's selection) ----

  type WrapOp = { kind: "wrap"; before: string; after: string; placeholder: string };
  type InsertOp = { kind: "insert"; text: string };

  const applyToFocused = (op: WrapOp | InsertOp) => {
    if (!focused) return;
    const input = cellRefs.current.get(cellKey(focused));
    if (!input) return;
    const current = readCellValue(focused);
    const start = input.selectionStart ?? current.length;
    const end = input.selectionEnd ?? current.length;

    let next: string;
    let newSelStart: number;
    let newSelEnd: number;
    if (op.kind === "insert") {
      next = current.slice(0, start) + op.text + current.slice(end);
      newSelStart = newSelEnd = start + op.text.length;
    } else {
      const inner = current.slice(start, end) || op.placeholder;
      const wrapped = `${op.before}${inner}${op.after}`;
      next = current.slice(0, start) + wrapped + current.slice(end);
      newSelStart = start + op.before.length;
      newSelEnd = newSelStart + inner.length;
    }
    writeCellValue(focused, next);
    // Re-focus + restore selection after React commits the state change.
    requestAnimationFrame(() => {
      input.focus();
      try {
        input.setSelectionRange(newSelStart, newSelEnd);
      } catch {
        /* Some input types reject setSelectionRange — fine to skip. */
      }
    });
  };

  // ---- Keyboard navigation ----

  const focusCell = (addr: CellAddress) => {
    const input = cellRefs.current.get(cellKey(addr));
    if (input) {
      input.focus();
      // Park caret at end so Tab-walks feel like a spreadsheet.
      const len = readCellValue(addr).length;
      try {
        input.setSelectionRange(len, len);
      } catch {
        /* skip */
      }
    }
  };

  const moveFocus = (current: CellAddress, direction: 1 | -1) => {
    const cols = model.headers.length;
    const lastRow = model.rows.length - 1;
    let row = current.row;
    let col = current.col + direction;
    if (col >= cols) {
      col = 0;
      row += 1;
    } else if (col < 0) {
      col = cols - 1;
      row -= 1;
    }
    if (row < -1) return; // before headers
    if (row > lastRow) return; // past last body row
    focusCell({ row, col });
  };

  const onCellKeyDown = (
    e: React.KeyboardEvent<HTMLDivElement>,
    addr: CellAddress,
  ) => {
    if (e.key === "Tab") {
      e.preventDefault();
      moveFocus(addr, e.shiftKey ? -1 : 1);
    }
    // Enter is intentionally NOT bound — cells are multi-line so Enter
    // creates an in-cell line break (the "6.8% \n {+0.6%}" pattern).
    // Adding a row is the + Row button's job.
  };

  // ---- Live preview markdown ----

  const previewMarkdown = useMemo(
    () => tableModelToMarkdown(model),
    [model],
  );

  // ---- Render ----

  const cellSx = {
    "& .MuiInputBase-root": {
      backgroundColor: "#fff",
      fontFamily: "Menlo, Monaco, 'Roboto Mono', monospace",
      fontSize: "0.82rem",
    },
    "& .MuiOutlinedInput-root": { borderRadius: "3px" },
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      PaperProps={{ sx: { borderRadius: 0, minHeight: "70vh" } }}
    >
      <DialogTitle
        sx={{
          backgroundColor: "#4c4c4c",
          color: "#fff",
          textTransform: "uppercase",
          fontSize: "0.85rem",
          fontWeight: 700,
          letterSpacing: "0.5px",
          py: 1.5,
          pr: 5,
        }}
      >
        {initial ? "Edit table" : "Insert table"}
        <IconButton
          aria-label="Close"
          onClick={onClose}
          sx={{ position: "absolute", right: 8, top: 8, color: "#fff" }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        {/* Cell toolbar — operates on whichever cell currently has focus.
            Tucked inside the dialog (rather than inside the grid) so the
            scrolling grid below it doesn't push it offscreen. */}
        <Stack
          direction="row"
          spacing={0.5}
          alignItems="center"
          sx={{
            px: 2,
            py: 1,
            borderBottom: "1px solid #eee",
            backgroundColor: "#fafafa",
          }}
        >
          <Typography
            sx={{
              fontSize: "0.7rem",
              fontWeight: 700,
              letterSpacing: "0.5px",
              textTransform: "uppercase",
              color: "text.secondary",
              mr: 1,
            }}
          >
            Cell
          </Typography>
          <Tooltip title="Bold (around selection)" arrow>
            <span>
              <IconButton
                size="small"
                disabled={!focused}
                onClick={() =>
                  applyToFocused({
                    kind: "wrap",
                    before: "**",
                    after: "**",
                    placeholder: "bold",
                  })
                }
              >
                <FormatBoldIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Italic (around selection)" arrow>
            <span>
              <IconButton
                size="small"
                disabled={!focused}
                onClick={() =>
                  applyToFocused({
                    kind: "wrap",
                    before: "*",
                    after: "*",
                    placeholder: "italic",
                  })
                }
              >
                <FormatItalicIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Box sx={{ width: 8 }} />
          <Tooltip title="Insert positive delta chip" arrow>
            <span>
              <Button
                size="small"
                disabled={!focused}
                onClick={() =>
                  applyToFocused({ kind: "insert", text: "{+0.0}" })
                }
                startIcon={<AddIcon fontSize="small" />}
                sx={{
                  minWidth: 0,
                  px: 1,
                  color: "#1b6e2c",
                  fontSize: "0.75rem",
                  fontWeight: 700,
                  textTransform: "none",
                }}
              >
                delta
              </Button>
            </span>
          </Tooltip>
          <Tooltip title="Insert negative delta chip" arrow>
            <span>
              <Button
                size="small"
                disabled={!focused}
                onClick={() =>
                  applyToFocused({ kind: "insert", text: "{-0.0}" })
                }
                startIcon={<RemoveIcon fontSize="small" />}
                sx={{
                  minWidth: 0,
                  px: 1,
                  color: "#a62020",
                  fontSize: "0.75rem",
                  fontWeight: 700,
                  textTransform: "none",
                }}
              >
                delta
              </Button>
            </span>
          </Tooltip>
          {!focused && (
            <Typography
              sx={{
                ml: 1,
                fontSize: "0.7rem",
                color: "text.secondary",
                fontStyle: "italic",
              }}
            >
              Click a cell to format
            </Typography>
          )}
        </Stack>

        {/* Table-level options (apply to the whole table). */}
        <Stack
          direction="row"
          alignItems="center"
          spacing={2}
          sx={{
            px: 2,
            py: 1,
            borderBottom: "1px solid #eee",
            backgroundColor: "#fdfdfd",
          }}
        >
          <Typography
            sx={{
              fontSize: "0.7rem",
              fontWeight: 700,
              letterSpacing: "0.5px",
              textTransform: "uppercase",
              color: "text.secondary",
            }}
          >
            Table
          </Typography>
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={!!model.center}
                onChange={(_e, checked) =>
                  setModel((prev) => setCenter(prev, checked))
                }
              />
            }
            label={
              <Typography sx={{ fontSize: "0.82rem" }}>
                Center on page
              </Typography>
            }
          />
          {(model.primaryCols?.length ?? 0) > 0 && (
            <Typography
              sx={{ fontSize: "0.78rem", color: "text.secondary" }}
            >
              Primary column{(model.primaryCols?.length ?? 0) > 1 ? "s" : ""}:{" "}
              {(model.primaryCols ?? []).map((c) => c + 1).join(", ")}
            </Typography>
          )}
        </Stack>

        {/* Grid */}
        <Box sx={{ p: 2, overflowX: "auto" }}>
          <ColumnControlsRow
            model={model}
            onSetAlignment={(c, a) =>
              setModel((prev) => setAlignment(prev, c, a))
            }
            onRemoveColumn={(c) => setModel((prev) => removeColumn(prev, c))}
            onAddColumn={() => setModel((prev) => addColumn(prev))}
            onTogglePrimary={(c) =>
              setModel((prev) => togglePrimaryCol(prev, c))
            }
          />

          {/* Header row */}
          <RowFrame onRemove={null} canRemove={false} addColumnSlotWidth>
            {model.headers.map((h, col) => (
              <CellInput
                key={`h-${col}`}
                value={h}
                isHeader
                addr={{ row: -1, col }}
                onChange={(v) =>
                  setModel((prev) => setHeader(prev, col, v))
                }
                onFocus={(a) => setFocused(a)}
                onBlur={() => {
                  /* keep focus tracking — clicking the toolbar would
                   *  otherwise lose it before the click fires. */
                }}
                onKeyDown={onCellKeyDown}
                inputRef={(el) => setRef({ row: -1, col }, el)}
                sx={cellSx}
              />
            ))}
          </RowFrame>

          {/* Body rows */}
          {model.rows.map((row, rowIdx) => (
            <RowFrame
              key={`r-${rowIdx}`}
              onRemove={() => setModel((prev) => removeRow(prev, rowIdx))}
              canRemove={model.rows.length > 1}
              addColumnSlotWidth
            >
              {row.map((cell, col) => (
                <CellInput
                  key={`c-${rowIdx}-${col}`}
                  value={cell}
                  isHeader={false}
                  addr={{ row: rowIdx, col }}
                  onChange={(v) =>
                    setModel((prev) => setCell(prev, rowIdx, col, v))
                  }
                  onFocus={(a) => setFocused(a)}
                  onBlur={() => {
                    /* see note above */
                  }}
                  onKeyDown={onCellKeyDown}
                  inputRef={(el) => setRef({ row: rowIdx, col }, el)}
                  sx={cellSx}
                />
              ))}
            </RowFrame>
          ))}

          {/* + Row */}
          <Box sx={{ mt: 1.5 }}>
            <Button
              size="small"
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => setModel((prev) => addRow(prev))}
              sx={{ textTransform: "none" }}
            >
              Add row
            </Button>
          </Box>
        </Box>

        {/* Live preview */}
        <Box
          sx={{
            borderTop: "1px solid #e0e0e0",
            backgroundColor: "#fff",
            px: 2,
            py: 2,
          }}
        >
          <Typography
            sx={{
              fontSize: "0.7rem",
              fontWeight: 700,
              letterSpacing: "0.5px",
              textTransform: "uppercase",
              color: "text.secondary",
              mb: 1,
            }}
          >
            Preview
          </Typography>
          <Box sx={{ overflowX: "auto" }}>
            <ArticleBody markdown={previewMarkdown} />
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 2, py: 1.5 }}>
        <Button onClick={onClose} sx={{ textTransform: "none" }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={() => {
            onInsert(tableModelToMarkdown(model));
            onClose();
          }}
          sx={{
            textTransform: "none",
            backgroundColor: "#4c4c4c",
            "&:hover": { backgroundColor: "#333" },
          }}
        >
          {initial ? "Replace table" : "Insert table"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ---- Sub-components -----------------------------------------------------

/**
 * Per-column controls row above the table — alignment toggle group +
 * delete-column button per column, plus an "Add column" button anchored
 * at the right edge to match the grid's column-add slot.
 */
function ColumnControlsRow({
  model,
  onSetAlignment,
  onRemoveColumn,
  onAddColumn,
  onTogglePrimary,
}: {
  model: TableModel;
  onSetAlignment: (col: number, alignment: TableAlignment) => void;
  onRemoveColumn: (col: number) => void;
  onAddColumn: () => void;
  onTogglePrimary: (col: number) => void;
}) {
  const primarySet = new Set(model.primaryCols ?? []);
  return (
    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
      {model.headers.map((_, col) => {
        const isPrimary = primarySet.has(col);
        return (
          <Stack
            key={`ctl-${col}`}
            direction="row"
            alignItems="center"
            spacing={0.25}
            sx={{ flex: 1, minWidth: 0 }}
          >
            <ToggleButtonGroup
              value={model.alignments[col]}
              exclusive
              size="small"
              onChange={(_e, v) => v && onSetAlignment(col, v as TableAlignment)}
              sx={{
                "& .MuiToggleButton-root": {
                  px: 0.5,
                  py: 0.25,
                  fontSize: "0.7rem",
                  border: "1px solid #ddd",
                },
                "& .Mui-selected": {
                  backgroundColor: "#4c4c4c !important",
                  color: "#fff !important",
                },
              }}
            >
              <ToggleButton value="left" aria-label="Align left">
                <FormatAlignLeftIcon sx={{ fontSize: "0.85rem" }} />
              </ToggleButton>
              <ToggleButton value="center" aria-label="Align center">
                <FormatAlignCenterIcon sx={{ fontSize: "0.85rem" }} />
              </ToggleButton>
              <ToggleButton value="right" aria-label="Align right">
                <FormatAlignRightIcon sx={{ fontSize: "0.85rem" }} />
              </ToggleButton>
            </ToggleButtonGroup>
            <Tooltip
              title={
                isPrimary
                  ? "Unset as primary column"
                  : "Style as primary column (header chrome)"
              }
              arrow
            >
              <IconButton
                size="small"
                onClick={() => onTogglePrimary(col)}
                sx={{
                  color: isPrimary ? "#CF0A2C" : "rgba(0,0,0,0.5)",
                  // Subtle fill on the selected state so the user can
                  // see at a glance which columns carry the flag,
                  // matching the alignment toggle's "selected" feel.
                  backgroundColor: isPrimary
                    ? "rgba(207, 10, 44, 0.08)"
                    : "transparent",
                }}
              >
                <StarOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Remove column" arrow>
              <span>
                <IconButton
                  size="small"
                  onClick={() => onRemoveColumn(col)}
                  disabled={model.headers.length <= 1}
                  sx={{ color: "#a62020" }}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        );
      })}
      {/* Slot aligned with the row-delete column in body rows + an
          "Add column" button. */}
      <Box sx={{ width: 32 }} />
      <Tooltip title="Add column" arrow>
        <IconButton
          size="small"
          onClick={onAddColumn}
          sx={{ backgroundColor: "#f0f0f0" }}
        >
          <AddIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Stack>
  );
}

/**
 * Row container — children are the cell inputs; trailing gutter is the
 * row-delete button (or an empty spacer for the header row). The
 * `addColumnSlotWidth` flag reserves an extra 32px to keep widths
 * aligned with the column-controls row above.
 */
function RowFrame({
  children,
  onRemove,
  canRemove,
  addColumnSlotWidth,
}: {
  children: React.ReactNode;
  onRemove: (() => void) | null;
  canRemove: boolean;
  addColumnSlotWidth: boolean;
}) {
  return (
    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.75 }}>
      {children}
      <Box sx={{ width: 32, display: "flex", justifyContent: "center" }}>
        {onRemove && (
          <Tooltip title="Remove row" arrow>
            <span>
              <IconButton
                size="small"
                onClick={onRemove}
                disabled={!canRemove}
                sx={{ color: "#a62020" }}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        )}
      </Box>
      {addColumnSlotWidth && <Box sx={{ width: 32 }} />}
    </Stack>
  );
}

/** Single cell input. Stays focused across model updates via inputRef.
 *  Multi-line so authors can stack a value over a delta chip
 *  (`6.8%\n{+0.6%}`) — Enter creates a line break inside the cell,
 *  Tab moves to the next cell. */
function CellInput({
  value,
  isHeader,
  addr,
  onChange,
  onFocus,
  onBlur,
  onKeyDown,
  inputRef,
  sx,
}: {
  value: string;
  isHeader: boolean;
  addr: CellAddress;
  onChange: (next: string) => void;
  onFocus: (addr: CellAddress) => void;
  onBlur: () => void;
  onKeyDown: (e: React.KeyboardEvent<HTMLDivElement>, addr: CellAddress) => void;
  inputRef: (el: HTMLTextAreaElement | HTMLInputElement | null) => void;
  sx?: object;
}) {
  return (
    <TextField
      size="small"
      fullWidth
      multiline
      minRows={1}
      // `maxRows` caps how tall a cell can grow on screen. Above that
      // the cell scrolls internally, which keeps the grid layout sane
      // when one cell has a paragraph of notes while others are
      // single-line. Source markdown is unaffected — only display.
      maxRows={6}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onFocus={() => onFocus(addr)}
      onBlur={onBlur}
      onKeyDown={(e) => onKeyDown(e, addr)}
      inputRef={inputRef}
      placeholder={isHeader ? "Header" : "Cell"}
      sx={{
        flex: 1,
        minWidth: 80,
        ...(isHeader && {
          "& .MuiInputBase-root": {
            fontWeight: 700,
          },
        }),
        ...sx,
      }}
    />
  );
}
